/**
 * One quote: sections, the margin slider, and acceptance.
 *
 * Acceptance is the biggest button in the app and the one with the most
 * consequences, so it confirms first and then reports exactly what it created
 * rather than silently navigating away.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, api, openDocument } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { day, money, percent, titleCase } from "../lib/format";
import type { QuoteOut, SendQuoteResult } from "../lib/types";
import { QuoteHeaderCard, QuoteSections, canEditQuote } from "../components/QuoteEditor";
import { Card, Chip, ErrorState, Loading, PageHeader } from "../components/ui";

export default function QuoteDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const isOffice = can(...OFFICE);

  const [margin, setMargin] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const { data: quote, isPending, error, refetch } = useQuery({
    queryKey: ["quote", id],
    queryFn: () => api.quotes.get(id),
    enabled: Boolean(id),
  });

  // Seed the slider from the quote once it lands, then leave it alone so
  // dragging it does not fight the refetch.
  useEffect(() => {
    if (quote && margin === null) setMargin(quote.target_margin_bp);
  }, [quote, margin]);

  const copy = useMutation({ mutationFn: (revision: boolean) => api.quotes.copy(id, revision), onSuccess: next => navigate(`/quotes/${next.id}`), onError: err => setProblem(err instanceof Error ? err.message : "Could not copy quote") });
  const reprice = useMutation({
    mutationFn: (bp: number) => api.quotes.reprice(id, bp),
    onSuccess: (fresh) => queryClient.setQueryData(["quote", id], fresh),
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't reprice."),
  });

  const accept = useMutation({
    mutationFn: () => api.quotes.accept(id, { take_deposit: true }),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ["quote", id] });
      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
    onError: (err) => {
      setConfirming(false);
      setProblem(err instanceof ApiError ? err.message : "Couldn't accept this quote.");
    },
  });

  if (isPending) return <Loading what="Loading the quote" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const accepted = quote.status === "accepted";
  const result = accept.data;

  return (
    <div className="page">
      <PageHeader eyebrow={quote.number} title={quote.title}>
        {quote.built_on_site ? "Built on site." : "Built in the office."}{" "}
        {quote.sections.length} section{quote.sections.length === 1 ? "" : "s"}.
      </PageHeader>

      {isOffice && <div className="btn-row" style={{ marginBottom: 16 }}>
        <button className="btn" disabled={copy.isPending} onClick={() => copy.mutate(false)}>Duplicate quote</button>
        {!quote.superseded_by_id && quote.status !== "accepted" && <button className="btn" disabled={copy.isPending} onClick={() => { if (window.confirm("Create a new draft revision? This version will remain available internally, but its customer link will stop accepting.")) copy.mutate(true); }}>New revision</button>}
        {quote.previous_quote_id && <Link to={`/quotes/${quote.previous_quote_id}`}>Previous version</Link>}
        {quote.superseded_by_id && <Link to={`/quotes/${quote.superseded_by_id}`}>Open replacement revision</Link>}
      </div>}
      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}

      {result && (
        <div className="notice notice-info" role="status">
          <b>Accepted.</b> That one signature created {result.job_ids.length} job
          {result.job_ids.length === 1 ? "" : "s"}, {result.asset_ids.length} asset
          {result.asset_ids.length === 1 ? "" : "s"}
          {result.contract_id ? ", a maintenance contract" : ""}
          {result.purchase_order_ids.length > 0
            ? `, ${result.purchase_order_ids.length} draft purchase order${result.purchase_order_ids.length === 1 ? "" : "s"}`
            : ""}
          {result.deposit_invoice_id ? " and a deposit invoice" : ""}.{" "}
          <Link to="/jobs">See the jobs</Link>.
        </div>
      )}

      <div className="split">
        <div className="split-main">
          <QuoteSections
            quote={quote}
            editable={isOffice && canEditQuote(quote)}
            onChanged={(next) => {
              queryClient.setQueryData<QuoteOut>(["quote", id], next);
              void queryClient.invalidateQueries({ queryKey: ["quotes"] });
            }}
          />
        </div>

        <aside className="split-side">
          <Card title="Price">
            <dl className="kv kv-money">
              <dt>Cost</dt>
              <dd className="mono">{money(quote.cost_cents)}</dd>
              <dt>Profit · {percent(quote.subtotal_cents ? Math.round((quote.subtotal_cents - quote.cost_cents) / quote.subtotal_cents * 10000) : 0, 1)} margin</dt>
              <dd className="mono">{money(quote.subtotal_cents - quote.cost_cents)}</dd>
              <dt>Discount included</dt><dd>{money(quote.discount_cents ?? 0)}</dd>
              <dt>Subtotal ex GST</dt><dd>{money(quote.subtotal_cents)}</dd>
              <dt>GST</dt>
              <dd className="mono">{money(quote.tax_cents)}</dd>
              <dt className="total">Total</dt>
              <dd className="mono total">{money(quote.total_cents)}</dd>
            </dl>

            {isOffice && canEditQuote(quote) && (
              <div style={{ marginTop: 16 }}>
                <label className="field">
                  <span>
                    Target margin <b className="mono">{percent(margin ?? quote.target_margin_bp, 0)}</b>
                  </span>
                  <input
                    className="slider"
                    type="range"
                    min={1800}
                    max={6200}
                    step={100}
                    value={margin ?? quote.target_margin_bp}
                    onChange={(e) => setMargin(Number(e.target.value))}
                    onPointerUp={() => margin !== null && reprice.mutate(margin)}
                    onKeyUp={() => margin !== null && reprice.mutate(margin)}
                  />
                </label>
                {reprice.isPending && <p className="tiny muted">Repricing…</p>}
                <p className="tiny muted">
                  Sets the default margin for lines without their own price or margin. Fixed selling prices stay unchanged. Costs and profit are internal only.
                </p>
              </div>
            )}
          </Card>

          {isOffice && canEditQuote(quote) && (
            <QuoteHeaderCard
              key={`${quote.title}|${quote.deposit_pct_bp}|${quote.valid_until ?? ""}`}
              quote={quote}
              onChanged={(next) => {
                queryClient.setQueryData<QuoteOut>(["quote", id], next);
              }}
            />
          )}

          <SendCard quote={quote} isOffice={isOffice} onChanged={() => void refetch()} />

          <Card title="Acceptance">
            {accepted ? (
              <p className="tiny">
                <Chip tone="ok" dot>
                  Accepted
                </Chip>{" "}
                Everything it implies has been created.
              </p>
            ) : quote.superseded_by_id ? (<p>A newer revision has replaced this quote.</p>) : !isOffice ? (
              <p className="tiny muted">
                Quotes are accepted by the office. Ask whoever handles approvals.
              </p>
            ) : confirming ? (
              <>
                <p className="tiny" style={{ marginBottom: 10 }}>
                  This creates the jobs, registers the assets, commits stock, drafts
                  any purchase order, starts the contract and raises the deposit —
                  in one go.
                </p>
                <div className="btn-row">
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => accept.mutate()}
                    disabled={accept.isPending}
                  >
                    {accept.isPending ? "Accepting…" : "Yes, accept it"}
                  </button>
                  <button className="btn" type="button" onClick={() => setConfirming(false)}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <button className="btn btn-primary btn-block" type="button" onClick={() => setConfirming(true)}>
                Accept quote
              </button>
            )}

            {quote.deposit_pct_bp > 0 && !accepted && (
              <p className="tiny muted" style={{ marginTop: 10 }}>
                A {percent(quote.deposit_pct_bp, 0)} deposit invoice will be raised —{" "}
                {money(
                  Math.round((quote.subtotal_cents * quote.deposit_pct_bp) / 10000) +
                    Math.round((Math.round((quote.subtotal_cents * quote.deposit_pct_bp) / 10000) * 1000) / 10000),
                )}{" "}
                including GST.
              </p>
            )}
          </Card>

          <Card title="Status">
            <Chip tone={accepted ? "ok" : "neutral"} dot>
              {titleCase(quote.status)}
            </Chip>
          </Card>
        </aside>
      </div>
    </div>
  );
}

/**
 * Emailing the link, and saying plainly what happened.
 *
 * The result is deliberately three-valued rather than a green tick: sent,
 * not configured, and tried-and-failed are different situations with different
 * fixes, and a UI that shows the same thing for all three is how a quote sits
 * in a drafts folder for a week while everyone assumes it went.
 */
function EmailBox({
  to,
  note,
  onTo,
  onNote,
  busy,
  outcome,
  onSend,
}: {
  to: string;
  note: string;
  onTo: (value: string) => void;
  onNote: (value: string) => void;
  busy: boolean;
  outcome: SendQuoteResult | null;
  onSend: () => void;
}) {
  const looksLikeAnAddress = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim());

  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--line-soft)", paddingTop: 12 }}>
      <label className="field">
        <span>Email it to</span>
        <input
          type="email"
          value={to}
          onChange={(e) => onTo(e.target.value)}
          placeholder="name@company.com.au"
          autoComplete="off"
          disabled={busy}
        />
      </label>
      <label className="field">
        <span>Note (optional)</span>
        <input
          value={note}
          onChange={(e) => onNote(e.target.value)}
          placeholder="As discussed on site this morning."
          disabled={busy}
        />
      </label>
      <button
        className="btn btn-block"
        type="button"
        onClick={onSend}
        disabled={busy || !looksLikeAnAddress}
      >
        {busy ? "Sending…" : "Send with the PDF"}
      </button>

      {outcome?.delivered_to && (
        <p className={`tiny ${outcome.delivered ? "" : "muted"}`} style={{ marginTop: 8 }} role="status">
          {outcome.delivered ? (
            <>Sent to {outcome.delivered_to}.</>
          ) : (
            <>
              <b>Not sent.</b> {outcome.delivery_detail}. The link above still works.
            </>
          )}
        </p>
      )}
    </div>
  );
}

/**
 * The link, and the two facts worth knowing about it: whether it went, and
 * whether anybody opened it.
 *
 * Keystone does not send the email. It hands over a URL, which is the part a
 * quoting system can be relied on for — an SMTP server that is misconfigured on
 * the day you need to send a quote is a worse failure than a copy and paste.
 */
function SendCard({
  quote,
  isOffice,
  onChanged,
}: {
  quote: QuoteOut;
  isOffice: boolean;
  onChanged: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [emailTo, setEmailTo] = useState("");
  const [note, setNote] = useState("");
  const [outcome, setOutcome] = useState<SendQuoteResult | null>(null);

  const send = useMutation({
    mutationFn: (payload?: { to?: string; note?: string }) => api.quotes.send(quote.id, payload),
    onSuccess: (result) => {
      setOutcome(result);
      onChanged();
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't send it."),
  });

  const rotate = useMutation({
    mutationFn: () => api.quotes.rotateLink(quote.id),
    onSuccess: onChanged,
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't rotate the link."),
  });

  // Absolute, because the whole point is that it gets pasted somewhere else.
  const url = quote.share_path ? `${window.location.origin}${quote.share_path}` : null;

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is denied often enough — over plain http, in some
      // embedded browsers — that failing silently would look like a bug. The
      // link is on screen and selectable either way.
      setProblem("Couldn't copy automatically — select the link and copy it.");
    }
  };

  return (
    <Card title="Customer link">
      {problem && (
        <p className="tiny" style={{ color: "var(--crit)" }} role="alert">
          {problem}
        </p>
      )}

      {url ? (
        <>
          <p className="tiny mono" style={{ wordBreak: "break-all", marginBottom: 10 }}>
            {url}
          </p>
          <div className="btn-row">
            <button className="btn btn-primary" type="button" onClick={() => void copy()}>
              {copied ? "Copied" : "Copy link"}
            </button>
            <a className="btn" href={quote.share_path ?? "#"} target="_blank" rel="noreferrer">
              Preview
            </a>
          </div>
          <EmailBox
            to={emailTo}
            note={note}
            onTo={setEmailTo}
            onNote={setNote}
            busy={send.isPending}
            outcome={outcome}
            onSend={() => send.mutate({ to: emailTo.trim(), note: note.trim() || undefined })}
          />

          <p className="tiny muted" style={{ marginTop: 10 }}>
            {quote.view_count > 0 ? (
              <>
                Opened {quote.view_count} time{quote.view_count === 1 ? "" : "s"}
                {quote.viewed_at ? `, first on ${day(quote.viewed_at)}` : ""}.
              </>
            ) : (
              "Not opened yet."
            )}
          </p>
          {isOffice && quote.status !== "accepted" && (
            <p className="tiny muted" style={{ marginTop: 8 }}>
              <button
                className="link-button"
                type="button"
                onClick={() => rotate.mutate()}
                disabled={rotate.isPending}
              >
                Rotate the link
              </button>{" "}
              if it went to the wrong address. The old one stops working
              immediately.
            </p>
          )}
        </>
      ) : (
        <>
          <button
            className="btn btn-primary btn-block"
            type="button"
            onClick={() => send.mutate(undefined)}
            disabled={send.isPending || quote.sections.length === 0}
          >
            {send.isPending ? "Preparing…" : "Create customer link"}
          </button>
          <p className="tiny muted" style={{ marginTop: 10 }}>
            Makes a page the customer can open and accept from their phone. Nothing
            is emailed — you get a link to send however you normally do.
          </p>
        </>
      )}

      {/* Available before the quote has ever been sent, so it can be checked
          on paper first. Same renderer as the customer's copy — proofreading
          has to be proofreading the real thing. */}
      <p className="tiny" style={{ marginTop: 12 }}>
        {/* A button, not a link: a link cannot carry the sign-in token, and
            opened the office a 401 instead of the PDF. */}
        <button
          type="button"
          className="linkish"
          onClick={() =>
            void openDocument(`/quotes/${quote.id}/pdf`).catch((err) =>
              setProblem(err instanceof ApiError ? err.message : "Couldn't open the PDF."),
            )
          }
        >
          Open the PDF
        </button>{" "}
        — exactly what the customer sees.
      </p>
    </Card>
  );
}
